/**
 * Stage one — hard gates.
 *
 * A non-negotiable cannot live in a percentage. A two-per-cent weight cannot
 * stop a wrong match. Gates run first and are binary — a failure means the
 * profile is never shown, not ranked low.
 *
 * Gates are evaluated in BOTH directions. A pair passes only if each side's
 * requirements are met by the other.
 */

import type { ProfileAnswers } from "../domain/answers";
import {
  normalizeCountryCode,
  resolvePreferredCountry,
  type PreferPartnerCountry,
} from "../domain/countries";
import { ANCESTRY_MODE } from "./un8.config";

export type GateCode =
  | "faith_level"
  | "children"
  | "substances"
  | "readiness"
  | "tattoos"
  | "ancestry"
  | "country";

export interface GateResult {
  passed: boolean;
  /** Gates that blocked the pair. Empty when `passed` is true. */
  failed: GateCode[];
}

/** Location prefs live on MatchProfile (not questionnaire). */
export interface CountryGateInput {
  country?: string | null;
  preferredPartnerCountry?: PreferPartnerCountry | null;
}

/** True when a gate cannot be judged because an answer is missing. */
function unknown(...vals: unknown[]): boolean {
  return vals.some((v) => v === null || v === undefined);
}

/**
 * One-directional check: does `other` satisfy what `self` requires?
 * Unanswered questions never block — an incomplete profile is a product
 * problem, not a mismatch.
 */
function oneWay(self: ProfileAnswers, other: ProfileAnswers): GateCode[] {
  const failed: GateCode[] = [];

  // 1 · minimum faith level required in a partner
  if (!unknown(self.requiredPartnerFaith, other.faith?.servingImportance)) {
    if ((other.faith!.servingImportance as number) < (self.requiredPartnerFaith as number)) {
      failed.push("faith_level");
    }
  }

  // 2 · substances — past use is only disqualifying if this member says so
  if (self.acceptsPastSubstanceUse === false) {
    const hist = other.history;
    const usedAny =
      hist?.usedMarijuana === true ||
      hist?.usedLsd === true ||
      hist?.usedOtherDrugs === true ||
      hist?.wasAddicted === true;
    if (usedAny) failed.push("substances");
  }

  // 3 · tattoos — skip when appearance block is missing (incomplete profile)
  if (
    !unknown(self.appearance?.acceptsPartnerTattoos, other.appearance?.hasTattoos) &&
    self.appearance!.acceptsPartnerTattoos === false &&
    other.appearance!.hasTattoos === true
  ) {
    failed.push("tattoos");
  }

  // 4 · ancestry — only a gate when explicitly configured as one
  const accepted = self.ancestry?.accepted;
  if (ANCESTRY_MODE === "gate" && accepted && accepted.length > 0) {
    const anyAccepted = accepted.includes("any");
    if (!anyAccepted) {
      const own = other.ancestry?.own ?? [];
      const overlap = own.some((o) => accepted.includes(o));
      if (own.length > 0 && !overlap) failed.push("ancestry");
    }
  }

  return failed;
}

/** Symmetric gates — incompatible for both sides at once. */
function mutual(a: ProfileAnswers, b: ProfileAnswers): GateCode[] {
  const failed: GateCode[] = [];

  // children — incompatible stances never surface
  if (!unknown(a.children, b.children)) {
    const wants = (s: ProfileAnswers["children"]) => s === "wants";
    const refuses = (s: ProfileAnswers["children"]) => s === "does_not_want";
    if ((wants(a.children) && refuses(b.children)) || (refuses(a.children) && wants(b.children))) {
      failed.push("children");
    }
  }

  // readiness — "not yet" members are matched only with each other
  const readyA = a.history?.readyForLastingRelationship;
  const readyB = b.history?.readyForLastingRelationship;
  if (!unknown(readyA, readyB) && readyA !== readyB) {
    failed.push("readiness");
  }

  return failed;
}

/** Full two-way gate evaluation for a candidate pair. */
export function evaluateGates(a: ProfileAnswers, b: ProfileAnswers): GateResult {
  if (!a || !b) {
    // Incomplete / missing questionnaire — never block (standing rule).
    return { passed: true, failed: [] };
  }
  const failed = Array.from(
    new Set([...oneWay(a, b), ...oneWay(b, a), ...mutual(a, b)]),
  );
  return { passed: failed.length === 0, failed };
}

/**
 * Preferred partner country — hard filter when "same" or a specific country.
 *
 * Standing rule: unanswered never blocks.
 * - Candidate with no country → pass (incomplete profile).
 * - Candidate with a different country → fail when preference is strict.
 * Evaluated one-way from viewer preference only (not mutual).
 */
export function evaluateCountryGate(
  self: CountryGateInput,
  other: CountryGateInput
): GateResult {
  const want = resolvePreferredCountry(self.country, self.preferredPartnerCountry);
  if (!want) return { passed: true, failed: [] };

  const theirs = normalizeCountryCode(other.country);
  if (!theirs) return { passed: true, failed: [] };
  if (theirs !== want) return { passed: false, failed: ["country"] };
  return { passed: true, failed: [] };
}

export const GATE_LABELS: Record<GateCode, string> = {
  faith_level: "Minimum faith level",
  children: "Children",
  substances: "Substances",
  readiness: "Readiness",
  tattoos: "Tattoos",
  ancestry: "Ancestry",
  country: "Partner country",
};
